import React, { FC, useEffect, useState } from 'react';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Button, { ButtonStyleType } from '~ComponentsImproved/Button';
import styled from '@emotion/styled';
import { Box } from '~Styles';
import { TroubleShootButtons, TroubleShootingTextWrapper } from '../AddedPrinter';
import Icon from '~ComponentsImproved/Icon';
import ArrowBack from '~Assets/icons/arrow_back.svg';
import { useI18n } from '~Hooks';

const GoBackButton = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  gap: 8px;
  font-weight: 600;
  color: #fff;
`;

// T is for having different types of next steps, to type error in the steps in the parent components
export type TroubleShootPageContent<T> = {
  title?: string;
  text?: string;
  images?: {
    src: string;
    height?: number;
  }[];
  buttons?: Array<{
    text: string;
    next?: T;
    action?: string;
    styleType?: ButtonStyleType;
  }>;
  custom?: JSX.Element;
};

interface Props<T extends string> {
  testPrint?: () => void;
  steps: {
    [key in T]: TroubleShootPageContent<T>;
  };
  currentStep: T;
  setCurrentStep: (step: T) => void;
}

const StepsComponent = <T extends string>({
  testPrint,
  steps,
  currentStep,
  setCurrentStep,
}: Props<T>) => {
  const { i18n } = useI18n();
  const firstStep = Object.keys(steps)[0];
  const [navigatedSteps, setNavigatedSteps] = useState<T[]>([firstStep as T]);

  useEffect(() => {
    setNavigatedSteps((prev) => {
      if (prev[prev.length - 1] !== currentStep) {
        return [...prev, currentStep];
      }
      return prev;
    });
  }, [currentStep]);

  const goBack = () => {
    if (navigatedSteps.length > 1) {
      const newSteps = [...navigatedSteps];
      newSteps.pop();
      setCurrentStep(newSteps[newSteps.length - 1]);
      setNavigatedSteps(newSteps);
    }
  };

  //Triggeraction updated after testPrint was made optional
  const triggerAction = (action: string) => {
    if (action === 'TEST_PRINT' && testPrint) {
      testPrint();
    }
  };

  return (
    <>
      {currentStep !== firstStep && navigatedSteps.length > 1 && (
        <GoBackButton onTouchStart={goBack}>
          <Icon src={ArrowBack} color="#fff" />
          {i18n('Printer.Troubleshooting.GoBack')}
        </GoBackButton>
      )}

      <TroubleShootingTextWrapper>
        {steps[currentStep].title && (
          <Heading level={1} color="#fff" bottom={16}>
            {steps[currentStep].title}
          </Heading>
        )}
        {steps[currentStep].text && (
          <Paragraph level={2} color="var(--colorBlueGray200)">
            {steps[currentStep].text}
          </Paragraph>
        )}
      </TroubleShootingTextWrapper>

      {steps[currentStep].images &&
        steps[currentStep].images.map((image) => (
          <Box marginTop="small">
            <img src={image.src} height={image.height ?? 'auto'} alt={steps[currentStep].title} />
          </Box>
        ))}

      {steps[currentStep].custom && steps[currentStep].custom}

      {steps[currentStep].buttons && (
        <TroubleShootButtons>
          {steps[currentStep].buttons.map((button) => (
            <Button
              key={button.text + currentStep}
              text={button.text}
              styleType={button.styleType ?? 'OK'}
              onTouchStart={() => {
                if (button.next) {
                  setCurrentStep(button.next);
                } else if (button.action) {
                  triggerAction(button.action);
                }
              }}
            />
          ))}
        </TroubleShootButtons>
      )}
    </>
  );
};

export default StepsComponent;
